import React, { FC } from 'react'
import { Container, Box } from '@moonpig/launchpad-components'
import { Breadcrumbs } from '@moonpig/web-core-nav'
import {
  ProductInfoProduct,
  ProductTabs,
  RecommendationsCarousel,
  PageTypeEnum,
  ModuleLayoutContainer,
  mapModule,
} from '@moonpig/web-shared-products'
import { styled, breakpointDown } from '@moonpig/launchpad-utils'
import { useStoreId } from '@moonpig/web-core-stores'
import { PeopleAlsoViewedCarousel } from '../PeopleAlsoViewedCarousel'
import { Breadcrumb, DepartmentInfo } from '../../types'
import { OfferBanner } from '../OfferBanner'
import { RelatedTermsCarousel } from '../RelatedTermsCarousel'
import { Department } from '../types'
import { useFindLocaleText } from '../../text-localisation'
import { useModuleConfiguration } from './configuration/useModuleConfiguration'

const BREAKPOINT = 'md'
const PEOPLE_ALSO_VIEWED_COUNT = 6

const StyledBreadCrumbs = styled(Breadcrumbs)`
  ${breakpointDown(BREAKPOINT)} {
    display: none;
  }
`

const isPurchasableProduct = (product: ProductInfoProduct | null): boolean =>
  product !== null && product.masterVariant.inStock

export type ProductWithRecommendations = ProductInfoProduct & {
  description: string
  frequentlyBoughtTogether: { products: ProductInfoProduct[] }
  peopleAlsoViewed: { products: (ProductInfoProduct | null)[] | null }
}

type ProductDetailsProps = {
  departmentInfo: DepartmentInfo
  product: ProductWithRecommendations
  breadcrumbsItems: Breadcrumb[]
}

export const ProductDetails: FC<ProductDetailsProps> = ({
  breadcrumbsItems,
  product,
  departmentInfo,
}) => {
  const localiseText = useFindLocaleText()
  const storeId = useStoreId()

  const { id, description, category } = product

  const peopleAlsoViewed =
    (product.peopleAlsoViewed.products?.filter(
      isPurchasableProduct,
    ) as ProductInfoProduct[]) || []

  const onConfigureModules = useModuleConfiguration()

  return (
    <>
      <OfferBanner offerText={departmentInfo.promotionBanner || undefined} />
      <Container limitWidth>
        <Box>
          <StyledBreadCrumbs items={breadcrumbsItems} />
        </Box>
        <Box mb={10} data-testid="web-product-info-panel">
          <ModuleLayoutContainer
            onConfigureModules={onConfigureModules}
            onMapModule={mapModule}
            context={{ pageType: PageTypeEnum.PDP }}
          />
        </Box>
        {category.department === 'GREETING_CARDS' && (
          <RecommendationsCarousel
            heading={localiseText('find.frequently_bought_together')}
            pageName={`product details | ${id.toLowerCase()}`}
            products={product.frequentlyBoughtTogether.products}
            carouselLength={2}
            carouselIndex={1}
          />
        )}
        <Box mb={10}>
          <ProductTabs
            description={description}
            productId={id}
            department={category.department as Department}
            deliveryInformation={departmentInfo.longDeliveryInfo}
            rating={product.rating}
          />
        </Box>
        {!!peopleAlsoViewed?.length && (
          <Box>
            <PeopleAlsoViewedCarousel
              parentProductId={id}
              products={peopleAlsoViewed.slice(0, PEOPLE_ALSO_VIEWED_COUNT)}
              region={storeId}
              backgroundColor="colorBackgroundSite"
              carouselLength={category.department === 'GREETING_CARDS' ? 2 : 1}
              carouselIndex={category.department === 'GREETING_CARDS' ? 2 : 1}
            />
          </Box>
        )}
        {product.relatedTerms && !!product.relatedTerms.terms.length && (
          <RelatedTermsCarousel
            terms={product.relatedTerms.terms}
            region={storeId}
          />
        )}
      </Container>
    </>
  )
}
